import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { AlertSeverityEnum, GrupoActivo, GrupoActivoPagingRes, UseMutationsParams } from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import { getUrlParamsFetch, handleAxiosError } from '@/shared';


const { get, post, put, remove } = erpApi();

///* tanSctack query
export const usefetchGrupoActivos = (params?: GetGrupoActivoParams) => {
  return useQuery({
    queryKey: ['GrupoActivo', ...Object.values(params || {})],
    queryFn: async () => {
      return getGrupoActivos(params);
    },
  });
};

export const useGetGrupoActivo = (id: string) => {
  return useQuery({
    queryKey: ['GrupoActivo', id],
    queryFn: async () => {
      return getGrupoActivo(id);
    },
  });
};

export const useCreateGrupoActivo = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createGrupoActivo,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['GrupoActivo'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Grupo activo creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateGrupoActivo = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateGrupoActivo,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['GrupoActivo'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Grupo Activo actualizada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};
export const useDeleteGrupoActivos = () => {
  const queryClient = useQueryClient();
  const setConfirmDialogIsOpen = useUiStore.getState().setConfirmDialogIsOpen;

  return useMutation({
    mutationFn: async (id: string) => removeGrupoActivos(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['grupo-activos'] });
      setConfirmDialogIsOpen(false);
    },
    onError: (error) => {
      handleAxiosError(error);
    },
  });
};
///* axios
export type GetGrupoActivoParams = {
  page?: number;
  page_size?: number;
  id_grupo_activo?: string;
  codigo?: string;
  descripcion?: string;
  cuenta_contable_debe?: string;
  cuenta_contable_haber?: string;
  porcentaje_depreciacion?: number;
  estado?: boolean;
};

export type CreateGrupoActivoParams = Omit<GrupoActivo, 'id_grupo_activo'>;
export type UpdateGrupoActivoParams = {
  id: string;
  data: Omit<GrupoActivo, 'id_grupo_activo'>;
};

export const getGrupoActivos = async (params?: GetGrupoActivoParams) => {
  const queryParams = getUrlParamsFetch({ ...params });

  return get<GrupoActivoPagingRes>(
    `${urlBackend}api/v1/grupo-activo/?${queryParams}`,
    true
  );
};

export const getGrupoActivo = async (id: string) => {
  try {
    return await get<GrupoActivo>(`${urlBackend}api/v1/grupo-activo/${id}/`, true);
  }
  catch (error) {
    return handleAxiosError(error);
  }
};

export const createGrupoActivo = async (data: CreateGrupoActivoParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<GrupoActivo>(`${urlBackend}api/v1/grupo-activo/`, data, true);
};

export const updateGrupoActivo = async ({ id, data }: UpdateGrupoActivoParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<GrupoActivo>(`${urlBackend}api/v1/grupo-activo/${id}/`, data, true);
};
export const removeGrupoActivos = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await remove<GrupoActivo>(`${urlBackend}api/v1/grupo-activo/${id}/`, true);
  setAlert(true, res.message, res.status);
  return res;
}